import React, { useCallback } from 'react';
import { TreeNode, NewEntryDialogState, TreeIndexMap } from '../../types';
import { findParentId, addChild, getFileTemplate, getTemplateDefaultName, FileTemplateKey } from '../../utils';

type NewEntryDialogProps = {
  dialog: NewEntryDialogState;
  entryName: string;
  entryTemplate: FileTemplateKey;
  tree: TreeNode[];
  treeIndex: TreeIndexMap;
  onChangeName: (name: string) => void;
  onChangeTemplate: (template: FileTemplateKey) => void;
  onClose: () => void;
  onApplyTreeUpdate: (tree: TreeNode[]) => void;
  setSelectedNodeId: (id: string | null) => void;
  setWorkingFiles: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setOpenTabs: React.Dispatch<React.SetStateAction<string[]>>;
  setActiveFile: (file: string | null) => void;
  setCode: (code: string) => void;
};

export function NewEntryDialog({
  dialog,
  entryName,
  entryTemplate,
  tree,
  treeIndex,
  onChangeName,
  onChangeTemplate,
  onClose,
  onApplyTreeUpdate,
  setSelectedNodeId,
  setWorkingFiles,
  setOpenTabs,
  setActiveFile,
  setCode,
}: NewEntryDialogProps) {
  const handleCreate = useCallback(() => {
    const name = entryName.trim();
    if (!name) return;
    const target = dialog.parent;
    const parentId = target ? (target.type === 'folder' ? target.id : findParentId(tree, target.id)) : null;
    const parentNode = parentId ? treeIndex.byId[parentId] : null;
    const pathBase = parentNode?.path || '';
    const id = `${dialog.type}-${Date.now()}`;
    const templateContent = dialog.type === 'file' ? getFileTemplate(entryTemplate, name) : '';
    const newNode: TreeNode = {
      id,
      name,
      path: pathBase ? `${pathBase}/${name}` : name,
      type: dialog.type === 'file' ? 'file' : 'folder',
      children: dialog.type === 'folder' ? [] : undefined,
    };
    const updated = addChild(tree, parentId, newNode);
    onApplyTreeUpdate(updated);
    setSelectedNodeId(id);
    if (dialog.type === 'file') {
      setWorkingFiles((prev) => ({ ...prev, [id]: templateContent }));
      setOpenTabs((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setActiveFile(id);
      setCode(templateContent);
    }
    onClose();
  }, [
    dialog,
    entryName,
    entryTemplate,
    tree,
    treeIndex,
    onApplyTreeUpdate,
    setSelectedNodeId,
    setWorkingFiles,
    setOpenTabs,
    setActiveFile,
    setCode,
    onClose,
  ]);

  if (!dialog.open) return null;

  return (
    <div className="goto-overlay" onClick={onClose}>
      <div className="goto-modal" onClick={(e) => e.stopPropagation()}>
        <div className="goto-header">
          <div>
            <p className="eyebrow">{dialog.type === 'file' ? 'Yeni dosya' : 'Yeni klasör'}</p>
            <h3>Create {dialog.type}</h3>
          </div>
        </div>
        <input
          className="goto-input"
          placeholder={dialog.type === 'file' ? 'NewFile.tsx' : 'NewFolder'}
          value={entryName}
          onChange={(e) => onChangeName(e.target.value)}
        />
        {dialog.type === 'file' && (
          <div className="goto-list">
            <label className="muted" style={{ padding: '4px 2px' }}>Template</label>
            <select
              value={entryTemplate}
              onChange={(e) => {
                const next = e.target.value as FileTemplateKey;
                onChangeTemplate(next);
                onChangeName(getTemplateDefaultName(next));
              }}
              className="goto-input"
            >
              <option value="component">React Component</option>
              <option value="hook">Custom Hook</option>
              <option value="api">Lodos API client</option>
              <option value="sandbox">IDE sandbox example</option>
              <option value="runner">Logistics runner</option>
              <option value="empty">Empty file</option>
            </select>
          </div>
        )}
        <div className="log-filters">
          <button className="pill small subtle" onClick={onClose}>
            Cancel
          </button>
          <button className="pill small success" onClick={handleCreate}>
            Create
          </button>
        </div>
      </div>
    </div>
  );
}
